import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { LogIn } from "lucide-react";
import axios from "axios";

const VerifyEmail = () => {
  const { token } = useParams();
  const [status, setStatus] = useState("loading"); // 'loading' | 'success' | 'error'
  const [message, setMessage] = useState("");

  const BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

  useEffect(() => {
    if (!token) return;

    let isMounted = true;

    const verifyEmail = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/auth/verify-email/${token}`, {
          withCredentials: true,
        });

        if (!isMounted) return;

        setMessage(res.data.message || "Email verified successfully!");
        setStatus("success");
      } catch (error) {
        if (!isMounted) return;

        const msg = error.response?.data?.message;

        // Treat "already verified" as success
        if (msg?.toLowerCase().includes("already verified")) {
          setMessage(msg);
          setStatus("success");
        } else {
          console.error("Verification error:", error);
          setMessage(msg || "The verification link is invalid or has expired.");
          setStatus("error");
        }
      }
    };

    verifyEmail();

    return () => {
      isMounted = false;
    };
  }, [token, BASE_URL]);

  return (
    <div className="flex flex-col items-center justify-center h-[80vh] bg-gray-50 px-4">
      <div className="bg-white shadow-md rounded-lg max-w-md w-full p-8 text-center border border-gray-200">
        {status === "loading" && (
          <>
            <div className="animate-spin h-10 w-10 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            <h2 className="text-xl font-semibold text-gray-700">Verifying your email...</h2>
            <p className="text-gray-500 mt-2">Please wait a moment</p>
          </>
        )}

        {status === "success" && (
          <>
            <div className="flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mx-auto mb-4">
              <svg
                className="w-8 h-8 text-green-500"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-green-600">Email Verified!</h2>
            <p className="text-gray-600 mt-2">{message}</p>
            <Link
              to="/signin"
              className="mt-6 w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-md flex items-center justify-center gap-2 transition duration-200"
            >
              <LogIn className="w-4 h-4" /> Go to Sign In
            </Link>
          </>
        )}

        {status === "error" && (
          <>
            <div className="flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mx-auto mb-4">
              <svg
                className="w-8 h-8 text-red-500"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-red-600">Verification Failed!</h2>
            <p className="text-gray-600 mt-2">{message}</p>
            <Link
              to="/register"
              className="mt-6 w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-md flex items-center justify-center gap-2 transition duration-200"
            >
              Go to Register
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default VerifyEmail;
